/**
 * O roteiro da demonstração (`O-10`, ADR-064): os dez cenários de homologação do cronograma
 * (C01–C10), cada um com o resultado esperado e o passo a passo na tela — o **modo livre**, que
 * não depende de quem apresenta.
 *
 * "Visto" vem do servidor e quer dizer que o fato do cenário **aconteceu** nesta organização,
 * ao vivo; a massa fabricada da demonstração não conta. A tela não marca nada à mão.
 *
 * Só para quem controla a demonstração (o apresentador e o administrador): os passos pedem os
 * controles dele.
 */
import { useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type { RoteiroDaDemonstracao as Roteiro } from "../api/tipos";
import { Badge, Estado } from "./base";

function quando(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function RoteiroDaDemonstracao({
  atualizadoEm,
}: {
  /** Muda quando a fila recarrega: o que acabou de acontecer aparece como visto. */
  atualizadoEm?: string | null;
}) {
  const [roteiro, definirRoteiro] = useState<Roteiro | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<Roteiro>("/tollvision/roteiro")
      .then((r) => {
        if (!vivo) return;
        definirRoteiro(r);
        definirErro(null);
      })
      .catch(
        (falha) =>
          vivo &&
          definirErro(
            falha instanceof ErroDaApi
              ? falha.message
              : "Não foi possível ler o roteiro da demonstração.",
          ),
      );
    return () => {
      vivo = false;
    };
  }, [atualizadoEm]);

  if (erro) return <Estado tipo="erro" titulo={erro} />;
  if (roteiro === null)
    return <Estado tipo="carregando" titulo="Lendo o roteiro…" />;

  return (
    <section
      id="roteiro-da-demonstracao"
      aria-label="Roteiro da demonstração"
      className="grid gap-2 rounded-cartao border border-linha bg-painel p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge cor={roteiro.vistos === roteiro.total ? "verde" : "azul"}>
          {`${roteiro.vistos} de ${roteiro.total} vistos`}
        </Badge>
        <span className="text-[11.5px] text-tinta-2">
          Os dez cenários de homologação. Cada um fica visto quando acontece
          nesta organização — o que a massa da demonstração já trazia não conta.
        </span>
      </div>
      <ol className="grid gap-1.5 xl:grid-cols-2">
        {roteiro.cenarios.map((c) => (
          <li key={c.codigo}>
            <details className="rounded-cartao border border-linha-suave bg-painel-2 px-2.5 py-1.5">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-[12px]">
                <span className="mono font-bold text-tinta-3">{c.codigo}</span>
                <span className="font-semibold text-tinta">{c.titulo}</span>
                <span className="ml-auto">
                  {c.visto_em ? (
                    <Badge cor="verde">{`visto ${quando(c.visto_em)}`}</Badge>
                  ) : (
                    <Badge cor="cinza">a ver</Badge>
                  )}
                </span>
              </summary>
              <div className="grid gap-1.5 pt-1.5 text-[11.5px] text-tinta-2">
                <p>
                  <span className="text-tinta-3">{c.transicao}.</span>{" "}
                  {c.resultado}
                </p>
                <ol className="grid list-decimal gap-0.5 pl-4">
                  {c.passos.map((passo) => (
                    <li key={passo}>{passo}</li>
                  ))}
                </ol>
              </div>
            </details>
          </li>
        ))}
      </ol>
    </section>
  );
}
